/**
 * PATH: packages/email-hub/src/feature/stores/companyScopeStore.ts
 * FILE_ID: EMAILHUB-STORE-COMPANY-SCOPE-001
 * PURPOSE: Shared company-domain filter + display label for inbox triage.
 * WHY: InboxReaderPane and InboxEmailRows both scoped-to-company with
 *      duplicated setters; the CompanyContextDrawer also renames the label
 *      and must update the active filter pill without a full list reload.
 * FLOW: filterCompany(domain, label?) → store → useInboxState / pills.
 * RELATED: emailFilterStore.ts, useInboxState.ts, CompanyContextDrawer.tsx
 */
import { create } from 'zustand'

export interface CompanyScopeState {
  /** Lower-cased sender domain currently filtering the inbox, or ''. */
  companyDomain: string
  /** Operator-facing label for the active company filter pill. */
  companyLabel: string
  setCompanyDomain: (domain: string, label?: string) => void
  setCompanyLabel: (label: string) => void
  /**
   * Scope the inbox to a company: clear person-from filter is the caller's
   * job; this only owns domain + label.
   */
  filterCompany: (domain: string, label?: string) => void
  clearCompany: () => void
}

function normaliseDomain(domain: string): string {
  return (domain || '').trim().toLowerCase()
}

export const useCompanyScopeStore = create<CompanyScopeState>((set) => ({
  companyDomain: '',
  companyLabel: '',

  setCompanyDomain: (domain, label) => {
    const next = normaliseDomain(domain)
    set((s) => ({
      companyDomain: next,
      companyLabel: next ? (label?.trim() || s.companyLabel || next) : '',
    }))
  },

  setCompanyLabel: (label) => {
    set((s) => {
      if (!s.companyDomain) return s
      return { companyLabel: label.trim() || s.companyDomain }
    })
  },

  filterCompany: (domain, label) => {
    const next = normaliseDomain(domain)
    set({
      companyDomain: next,
      companyLabel: next ? (label?.trim() || next) : '',
    })
  },

  clearCompany: () => set({ companyDomain: '', companyLabel: '' }),
}))

/** Pure I→O: resolve domain for a filter-company click. */
export function companyDomainFromEmail(email: {
  company_domain?: string | null
  from_address?: string | null
}): string {
  const direct = (email.company_domain || '').trim().toLowerCase()
  if (direct) return direct
  const addr = email.from_address || ''
  const at = addr.lastIndexOf('@')
  return at >= 0 ? addr.slice(at + 1).trim().toLowerCase() : ''
}
